import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useUserProfile } from '../hooks/useUserProfile';
import { useMFA } from '../hooks/useMFA';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const {
    userProfile,
    profileLoading,
    isDarkMode,
    loadProfile,
    clearProfile,
    updateProfile: updateProfileData,
    toggleDarkMode: toggleDarkModeForUser,
  } = useUserProfile();

  const mfa = useMFA();

  // Auth state handler
  const handleAuthChange = (session) => {
    setUser(session?.user ?? null);
    setLoading(false);
    if (session?.user) {
      loadProfile(session?.user?.id); // Fire-and-forget
    } else {
      clearProfile();
    }
  };

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (
        error?.message?.includes('Refresh Token Not Found') ||
        error?.message?.includes('Invalid Refresh Token')
      ) {
        supabase?.auth?.signOut();
        setUser(null);
        clearProfile();
        setLoading(false);
        return;
      }
      handleAuthChange(session);
    })?.catch(() => {
      // Network error (e.g. "Failed to fetch") — treat as unauthenticated
      setUser(null);
      clearProfile();
      setLoading(false);
    });

    // Auth state subscription
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' && !session) {
        supabase?.auth?.signOut();
        setUser(null);
        clearProfile();
        setLoading(false);
        return;
      }
      if (event === 'SIGNED_OUT') {
        setUser(null);
        clearProfile();
        setLoading(false);
        return;
      }
      if (event === 'TOKEN_REFRESH_FAILED') {
        supabase?.auth?.signOut();
        setUser(null);
        clearProfile();
        setLoading(false);
        return;
      }
      handleAuthChange(session);
    });

    return () => subscription?.unsubscribe();
  }, []);

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        clearProfile();
      }
      return { error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  // Sets a new password AND clears the "must change password" flag in the SAME call, so the
  // flag can't be dropped without actually setting a password. Supabase emits USER_UPDATED,
  // which handleAuthChange picks up — the gate in ProtectedRoute then releases on its own,
  // with no reload. Used by the forced first-sign-in screen for provisioned client logins.
  const updatePassword = async (password) => {
    try {
      const { data, error } = await supabase?.auth?.updateUser({
        password,
        data: { must_change_password: false },
      });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  };

  const updateProfile = async (updates) => {
    return updateProfileData(user?.id, updates);
  };

  const toggleDarkMode = async () => {
    return toggleDarkModeForUser(user?.id, isDarkMode);
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    isDarkMode,
    toggleDarkMode,
    signIn,
    signOut,
    updatePassword,
    updateProfile,
    isAuthenticated: !!user,
    // MFA methods
    enrollMFA: mfa?.enrollMFA,
    verifyMFAEnrollment: mfa?.verifyMFAEnrollment,
    verifyMFALogin: mfa?.verifyMFALogin,
    unenrollMFA: mfa?.unenrollMFA,
    getMFAFactors: mfa?.getMFAFactors,
    getAAL: mfa?.getAAL,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };